import { createAsyncThunk } from '@reduxjs/toolkit'
import {
  getScheduleClose,
  postScheduleClose,
  putScheduleClose,
  deleteScheduleClose,
  type PostScheduleClosePayloadType,
} from 'features/Location/services/globalScheduleClose'
import { getLocationTypeList, getLocationListByType } from 'features/Location/services/location'
import { getCleanQueryObj } from 'utils/queryParamsURL'
import { callErrorMsg } from 'helpers/errorMsg'
import { getCloseMessage, getCloseMessageById } from 'features/Location/services/closeMessageConfig'
import {
  getAdjacentConfig,
  getAdjacentHubDetail,
  getAdjacentHubList,
} from 'features/Location/services/adjacentHub'
import { setAdjacentLocationNames } from 'features/Location/redux/adjacentHub/list'
import {
  getValidAdjacentLocations,
  toAdjacentLocationNameMap,
} from 'features/Location/utils/adjacentHub'
import {
  SCHEDULE_CLOSE_SLICE_NAME,
  setIsLoading,
  setLocationTypes,
  setSchedules,
  setLocations,
  setCloseReason,
  setAdjacentHubPreview,
  setIsLoadingAdjacentHubPreview,
  setNearbyHubIdsByLocationId,
  setAdjacentConfig,
} from './slice'

const ADJACENT_HUB_PREVIEW_LIMIT = 100

// Loads nearby hubs for every closed location not cached yet: IDs go to this slice,
// names to the shared Nearby Hub Master name cache. A location without an
// adjacent config (or a failed lookup) is cached as [] and rendered as "-".
// `force` skips the cache check so cached locations are fetched again.
export const fetchNearbyHubByLocation = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchNearbyHubByLocation`,
  async (
    { locationIds, force = false }: { locationIds: number[]; force?: boolean },
    { dispatch, getState },
  ) => {
    const {
      scheduleClose: { nearbyHubIdsByLocationId },
    } = getState() as StoreStateType
    const missingIds = locationIds.filter(
      (id, index) => locationIds.indexOf(id) === index && (force || !nearbyHubIdsByLocationId[id]),
    )
    if (!missingIds.length) return

    const results = await Promise.allSettled(missingIds.map((id) => getAdjacentHubDetail(id)))
    const adjacentLocations = results.map((result) =>
      result.status === 'fulfilled' ? result.value.data.data.adjacent_location : [],
    )
    const nearbyHubIds = Object.fromEntries(
      missingIds.map((id, index) => [
        id,
        getValidAdjacentLocations(adjacentLocations[index]).map(({ location_id }) => location_id),
      ]),
    )

    dispatch(setNearbyHubIdsByLocationId(nearbyHubIds))
    dispatch(setAdjacentLocationNames(toAdjacentLocationNameMap(adjacentLocations.flat())))
  },
)

export const fetchScheduleClose = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchScheduleClose`,
  async (
    { forceRefetchNearbyHub = false }: { forceRefetchNearbyHub?: boolean } | undefined = {},
    { dispatch, getState },
  ) => {
    const {
      scheduleClose: { query },
    } = getState() as StoreStateType

    dispatch(setIsLoading(true))
    try {
      const {
        data: { data },
      } = await getScheduleClose(getCleanQueryObj(query))
      dispatch(setSchedules(data.global_scheduled_close))
      dispatch(
        fetchNearbyHubByLocation({
          locationIds: data.global_scheduled_close.flatMap((schedule) =>
            schedule.location.map(({ id }) => id),
          ),
          force: forceRefetchNearbyHub,
        }),
      )
    } catch (err) {
      callErrorMsg(err)
    }
    dispatch(setIsLoading(false))
  },
)

export const createScheduleClose = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/createScheduleClose`,
  async (payload: PostScheduleClosePayloadType) => {
    try {
      await postScheduleClose(payload)

      return true
    } catch (err) {
      callErrorMsg(err)
      return false
    }
  },
)

export const updateScheduleClose = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/updateScheduleClose`,
  async ({ id, payload }: { payload: PostScheduleClosePayloadType; id: string | number }) => {
    try {
      await putScheduleClose(id, payload)

      return true
    } catch (err) {
      callErrorMsg(err)
      return false
    }
  },
)

export const removeScheduleClose = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/removeScheduleClose`,
  async (id: string) => {
    try {
      await deleteScheduleClose(id)

      return true
    } catch (err) {
      callErrorMsg(err)
      return false
    }
  },
)

export const fetchLocationType = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchLocationType`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getLocationTypeList()

      dispatch(setLocationTypes(data.data))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchLocations = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchLocations`,
  async (type: string, { dispatch }) => {
    try {
      const { data } = await getLocationListByType(type)

      dispatch(setLocations(data))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

export const fetchCloseMessage = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchCloseMessage`,
  async (search: string, { dispatch }) => {
    try {
      const {
        data: { data },
      } = await getCloseMessage({ page: 0, limit: 20, search })
      dispatch(setCloseReason(data.close_reason))
    } catch (err) {
      callErrorMsg(err)
    }
  },
)

// Loads the adjacent hubs of every selected location so the dialog can preview
// which hubs will receive the rerouted orders during the closure.
export const fetchAdjacentHubPreview = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchAdjacentHubPreview`,
  async (locationIds: number[], { dispatch }) => {
    if (!locationIds.length) {
      dispatch(setAdjacentHubPreview([]))
      return
    }

    dispatch(setIsLoadingAdjacentHubPreview(true))
    try {
      const responses = await Promise.all(
        locationIds.map((locationId) =>
          getAdjacentHubList({
            params: {
              page: 1,
              limit: ADJACENT_HUB_PREVIEW_LIMIT,
              location_id: locationId,
            },
          }),
        ),
      )

      dispatch(
        setAdjacentHubPreview(
          responses.flatMap(({ data }) =>
            (data.data.adjacent || []).map((item) => ({
              hubName: item.location_name,
              adjacentHub: getValidAdjacentLocations(item.adjacent_location)
                .map(({ location_name }) => location_name)
                .join(', '),
            })),
          ),
        ),
      )
    } catch (err) {
      callErrorMsg(err)
      dispatch(setAdjacentHubPreview([]))
    }
    dispatch(setIsLoadingAdjacentHubPreview(false))
  },
)

export const fetchCloseMessageDetail = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchCloseMessageDetail`,
  async (id: number) => {
    try {
      const {
        data: { data },
      } = await getCloseMessageById(id)
      return data
    } catch (err) {
      callErrorMsg(err)
      return null
    }
  },
)

// A failed fetch leaves the config null, which hides the reroute section.
export const fetchAdjacentConfig = createAsyncThunk(
  `${SCHEDULE_CLOSE_SLICE_NAME}/fetchAdjacentConfig`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getAdjacentConfig()
      dispatch(setAdjacentConfig(data.data))
    } catch (err) {
      callErrorMsg(err)
      dispatch(setAdjacentConfig(null))
    }
  },
)
